import React from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { useSiteData } from '../context/SiteDataContext';
import { MapPin, Phone, Clock, Mail } from 'lucide-react';

const ContactPage = () => {
  const { companyInfo, pageContent } = useSiteData();
  const hero = pageContent?.contactHero || {};

  return (
    <div className="min-h-screen bg-black font-sans" data-testid="contact-page">
      <Helmet>
        <title>Contact & Rendez-vous | Ecotint & Reprog - Eugénie les Bains (40)</title>
        <meta name="description" content="Contactez Ecotint & Reprog au 06 72 44 37 22. Atelier situé 130 route de Tourbouret, 40320 Eugénie les Bains. Devis gratuit, rendez-vous sur mesure." />
        <link rel="canonical" href="https://ecotint-reprog.fr/contact" />
      </Helmet>
      <Header />
      
      {/* Hero */}
      <section className="pt-28 pb-12 sm:pt-32 sm:pb-20 bg-black">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 text-center">
          <span className="text-red-500 text-sm font-medium uppercase tracking-[0.2em]">
            {hero.category || 'Contact & RDV'}
          </span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mt-4 mb-4 sm:mb-6">
            {hero.title || 'Contactez-'}<span className="text-red-500">{hero.titleAccent || 'nous'}</span>
          </h1>
          <p className="text-base sm:text-xl text-white/60 max-w-2xl mx-auto">
            {hero.desc || "Pour un devis personnalisé ou prendre rendez-vous, n'hésitez pas à nous contacter."}
          </p>
        </div>
      </section>

      {/* Map and Email CTA */}
      <section className="py-12 sm:py-20 bg-neutral-950">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Map */}
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6">
                <span className="text-red-500">Localisation</span>
              </h2>
              <div className="aspect-[4/3] border border-white/10 rounded-2xl overflow-hidden">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2910.9041947461473!2d-0.3878386!3d43.6916667!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd569a1c8e4cf11f%3A0x6f9e8d5d6a8c0e0!2s130%20Route%20de%20Tourbouret%2C%2040320%20Eug%C3%A9nie-les-Bains!5e0!3m2!1sfr!2sfr!4v1699999999999!5m2!1sfr!2sfr"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  title="Localisation"
                />
              </div>
              <p className="text-white/40 text-sm mt-4">
                Astuce : Tapez "ecotint40" sur Google Maps pour nous trouver.
              </p>
            </div>

            {/* Email CTA Card */}
            <div className="flex items-center">
              <div className="w-full bg-white/5 border border-white/10 rounded-2xl p-6 sm:p-10 text-center">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-red-600 flex items-center justify-center mx-auto mb-6">
                  <Mail size={32} className="text-white sm:hidden" />
                  <Mail size={40} className="text-white hidden sm:block" />
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-white mb-3">
                  Envoyez-nous un <span className="text-red-500">email</span>
                </h2>
                <p className="text-white/50 text-sm sm:text-base mb-8 max-w-md mx-auto">
                  Décrivez votre projet ou posez vos questions. Nous vous répondrons dans les meilleurs délais.
                </p>
                <a
                  href={`mailto:${companyInfo.email || 'contact@ecotint-reprog.fr'}`}
                  data-testid="mailto-button"
                  className="inline-flex items-center justify-center gap-3 bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-full font-medium transition-all duration-300 text-base sm:text-lg w-full sm:w-auto"
                >
                  <Mail size={20} />
                  <span>Nous écrire</span>
                </a>
                <p className="text-white/30 text-xs sm:text-sm mt-4">
                  {companyInfo.email || 'contact@ecotint-reprog.fr'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Cards */}
      <section className="py-8 sm:py-12 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            <a
              href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}
              data-testid="phone-card"
              className="bg-red-600 hover:bg-red-700 p-6 sm:p-8 text-center transition-colors rounded-2xl group"
            >
              <Phone size={24} className="mx-auto mb-3 sm:mb-4 text-white sm:hidden" />
              <Phone size={28} className="mx-auto mb-3 sm:mb-4 text-white hidden sm:block" />
              <div className="text-white font-semibold text-base sm:text-lg">Téléphone</div>
              <div className="text-white/80 mt-1 sm:mt-2 text-sm sm:text-base">{companyInfo.phone}</div>
            </a>

            <div className="bg-white/5 border border-white/10 p-6 sm:p-8 text-center rounded-2xl" data-testid="address-card">
              <MapPin size={24} className="mx-auto mb-3 sm:mb-4 text-red-500 sm:hidden" />
              <MapPin size={28} className="mx-auto mb-3 sm:mb-4 text-red-500 hidden sm:block" />
              <div className="text-white font-semibold text-base sm:text-lg">Adresse</div>
              <div className="text-white/60 mt-1 sm:mt-2 text-xs sm:text-sm">
                {companyInfo.address}<br />{companyInfo.city}
              </div>
            </div>

            <div className="bg-white/5 border border-white/10 p-6 sm:p-8 text-center rounded-2xl" data-testid="hours-card">
              <Clock size={24} className="mx-auto mb-3 sm:mb-4 text-red-500 sm:hidden" />
              <Clock size={28} className="mx-auto mb-3 sm:mb-4 text-red-500 hidden sm:block" />
              <div className="text-white font-semibold text-base sm:text-lg">Horaires</div>
              <div className="text-white/60 mt-1 sm:mt-2 text-xs sm:text-sm">Sur rendez-vous</div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
